/**
 * How often, and how expensively, the arena is allowed to draw.
 *
 * Nothing in here is about the game. The server owns every rule at a fixed 20 Hz and the client
 * interpolates between the frames it is sent; what this module decides is the *cost* of turning
 * those frames into pixels, which is the whole of what a phone's battery is being spent on. It
 * is pure and Phaser-free for `camera.ts`'s, `spectator.ts`'s and `palette.ts`'s reason: the
 * interesting part is which numbers each surface gets, and that is a thing to assert rather than
 * a thing to look at.
 *
 * **The shell was drawing as fast as the panel would let it.** `new Phaser.Game` passed no `fps`
 * block at all, so the loop ran off `requestAnimationFrame` at the display's refresh rate — 120
 * frames a second on a modern Android phone, of which sixty are a redraw of a world that has not
 * changed, because there is nothing above ~60 fps that a player of a 20 Hz game can act on. It
 * also asked for a multisampled WebGL context (`antialias: true`, which Phaser turns into
 * `antialiasGL: true`) and named no `powerPreference`. This module is the answer to all three.
 *
 * **THE CAP IS `fps.limit`, NOT `forceSetTimeOut`, AND THAT IS THE ONE REAL DECISION HERE.**
 * Both of them reduce the number of frames, and they are not equivalent:
 *
 * - `forceSetTimeOut` drives the whole loop off `setTimeout(…, 1000 / target)`. It is not
 *   aligned to the display at all, so its frames land wherever they land relative to vsync —
 *   which is judder on a panel of any refresh rate, and is what "capped frame rates look
 *   choppier" usually means.
 * - `fps.limit` leaves the loop on rAF and skips the *game step* until `1000 / limit` ms have
 *   accumulated (`TimeStep.stepLimitFPS`). A tick that does not fire costs the delta bookkeeping
 *   and a compare; the update and the render — the expensive halves — happen at the limit. And because it
 *   passes on whole rAF ticks, what it produces is always the refresh rate divided by a whole
 *   number: 60 out of 120, 45 out of 90, 48 out of 144. Every frame is still vsync-aligned and
 *   evenly spaced, which is exactly what `forceSetTimeOut` cannot promise.
 *
 * So the loop stays on rAF everywhere and the shell gets a limit. `forceSetTimeOut` is in the
 * profile as `false` on both surfaces rather than being left out, because "we do not use it" is
 * the decision this comment exists to record.
 *
 * **The limit is asked for a little above the rate it means to permit**, which is what
 * `FRAME_LIMIT_HEADROOM_FPS` is. The test is `accumulated delta >= 1000 / limit`, and on a 60 Hz
 * panel asking for exactly 60 puts both sides of it on the same floating-point number — so the
 * comparison becomes a coin flip and roughly every other frame is dropped, at 60 Hz, where
 * nothing needed dropping. Asking for 62 is asking for "no faster than about sixty": it clears
 * on a 60 Hz panel every time and still costs a 120 Hz one every second frame.
 *
 * **The MSAA context is dropped on the shell and the texture filtering is not.** Phaser has two
 * knobs behind one name: `antialias` chooses the texture sampler (linear or nearest) and
 * `antialiasGL` is what actually asks the browser for a multisampled drawing buffer. Turning
 * `antialias` off would take photo skins and flag chips down to nearest-neighbour for no GPU
 * saving worth having, so it stays on; `antialiasGL` goes, and it goes because of what the scale
 * mode already does. `Scale.RESIZE` sets the drawing buffer to the parent's size in *CSS*
 * pixels, so on a 3x phone the arena is already drawn at a ninth of the panel's pixels and then
 * upscaled by the compositor, whose own filtering is doing far more for a blob's edge than
 * multisampling a 1x buffer ever did. It is a per-frame cost on every single frame in exchange
 * for something that is smoothed over on the way to the screen.
 *
 * That is also why this module reads **no `devicePixelRatio` and no `screen`** — and takes no
 * render scale. There is no oversampling here to cut: the scale mode already renders at one
 * drawing pixel per CSS pixel, and going below that means CSS-stretching a smaller canvas, which
 * is a visible blur and a structural change to how the arena is sized. If a card ever wants it,
 * it is a change to the `scale` block, not a number this function could hand over.
 *
 * **The first input is whether this is the shell.** iOS and Android get the same answer — the
 * argument for a frame cap is the panel and the battery, and both have both — so there is no
 * platform branch to make, and a phone *browser* keeps today's profile because the drain that
 * was reported is the installed app and that is the surface this card measured.
 *
 * **The second is the player's own Battery saver switch, and it is the one that outranks the
 * surface.** It is off by default, so nothing above changes for anybody who never opens the
 * overlay settings; turned on it drops the cap to BATTERY_SAVER_TARGET_FPS *wherever it is
 * turned on*, a browser included — a laptop on its battery is as real as a phone on one, and a
 * switch that silently did nothing on the surface a player flipped it on would be a switch that
 * lies. It is a **fidelity** knob and never a visibility one: everything the arena draws it
 * still draws, less often and with less decoration. See `state/hud-settings.ts`.
 *
 * The cap is the one thing in this profile that has to be able to move while a game is running,
 * because the switch is in a modal a player opens mid-run. `applyFrameLimit` is that, and it is
 * here rather than in `GameCanvas` for this module's reason: what it does to Phaser's loop is a
 * thing to assert against a fake, not a thing to look at.
 */

/**
 * The most pixels per CSS pixel the arena will ever be drawn at.
 *
 * **Two is a quality decision with a cost ceiling on it.** A phone reports a device pixel ratio of
 * 2.6 to 3.5; rendering at all of it is 7 to 12 times the fragments of 1x for a difference nobody
 * can see past about 2, because the panel's own pixels are already below the eye's resolving power
 * at arm's length. Two is where the curve flattens: a blob's edge is resolved by four times the
 * samples it had, and the fill stays a quarter of what 3x would cost.
 */
export const MAX_RENDER_SCALE = 2;

/** What a render profile is decided from. An argument rather than a read, so no test needs Capacitor. */
export interface RenderEnvironment {
  /** `isNativePlatform()` — true only inside the iOS or Android shell, false in every browser. */
  native: boolean;
  /** `HudSettings.batterySaver`, the player's own switch. Off is today's behaviour. */
  batterySaver: boolean;
  /** `window.devicePixelRatio`. An argument for this module's reason: it is a thing to assert. */
  devicePixelRatio: number;
}

/** The `powerPreference` hint Phaser passes to `getContext('webgl2')`. */
export type RenderPowerPreference = 'default' | 'low-power' | 'high-performance';

/** Everything `GameCanvas` puts in the `fps` and `render` blocks of the game config. */
export interface RenderProfile {
  /** `fps.target`. What the loop considers optimal; it enforces nothing on its own. */
  targetFps: number;
  /** `fps.limit`. The rate the game step is actually allowed to run at; 0 is no limit. */
  frameLimit: number;
  /** `fps.forceSetTimeOut`. False on both surfaces — see the note above. */
  forceSetTimeOut: boolean;
  /** `fps.smoothStep`. Phaser's delta smoothing, left on. */
  smoothStep: boolean;
  /** `render.antialias`. The texture sampler: linear, always. */
  antialias: boolean;
  /** `render.antialiasGL`. The multisampled drawing buffer, dropped on the shell. */
  antialiasGL: boolean;
  /** `render.powerPreference`. */
  powerPreference: RenderPowerPreference;
  /** `render.roundPixels`. False on both surfaces — see below. */
  roundPixels: boolean;
  /**
   * Drawing-buffer pixels per CSS pixel. 1 is what `Scale.RESIZE` did on its own.
   *
   * **THIS IS WHY THE EDGES WERE UGLY, AND THE PREVIOUS COMMENT IN THIS FILE HAD IT EXACTLY
   * BACKWARDS.** It argued that dropping the multisampled buffer was free *because* the scale mode
   * already draws at one buffer pixel per CSS pixel and the compositor's own filtering would do
   * more for a blob's edge than multisampling a 1x buffer. What that describes is an arena drawn
   * at roughly a seventh of the phone's pixels and then magnified two and a half times: the
   * compositor is not smoothing an edge there, it is enlarging one. Every stair-step on a circle
   * got 2.6 times wider, and MSAA — the one thing that had been hiding it — had just been removed.
   * The player's report was that the circles looked "absolutely terrible" beside a native game's,
   * and they were right.
   *
   * The buffer is sized in device pixels now (capped at MAX_RENDER_SCALE), which is what a native
   * game does and the only thing that makes an edge genuinely crisp rather than smoothly wrong.
   */
  renderScale: number;
}

/**
 * The rate the shell is allowed to draw at.
 *
 * **Sixty, then thirty, then twenty, and back to thirty — and the last step is arithmetic rather
 * than taste.** Twenty was tried because it is exactly the room's tick, and it came back "a little
 * choppy" against a native game that is "perfectly smooth"; the ask was 25. **THERE IS NO 25.**
 * `fps.limit` passes on whole rAF ticks (`permittedFps`), so what a display can produce is its
 * refresh rate divided by a whole number, and since `MainActivity` asks Android for a 60 Hz panel
 * the only rungs that exist are 60, 30, 20, 15 and 12. Asking for 25 lands on 20 — the same
 * choppiness, believed to be a fix. The rung above 20 is 30 and that is where this sits.
 *
 * **Why twenty is choppier than the tick rate alone suggests.** Drawing at exactly the rate the
 * room ticks sounds ideal and is not: the socket's 20 Hz and the display's 20 Hz are two
 * independent clocks, so they drift against each other and the interpolator is sometimes handed
 * two frames of the same pair and sometimes skips one. Thirty decouples them — every drawn frame
 * has a fresh position to ease toward — which is most of what "perfectly smooth" is made of.
 *
 * The battery that costs is handed back by the switch below rather than by frames: see
 * `renderScaleFor`.
 *
 * A phone browser still gets the uncapped profile, for `renderProfile`'s stated reason: the drain
 * that was reported is the installed app, and that is the surface this was measured on.
 */
export const NATIVE_TARGET_FPS = 30;

/**
 * The rate Battery saver draws at — the deeper cut the line above leaves to the player.
 *
 * **Twenty, the same as the shell's own cap, and the switch earns its keep elsewhere now.** The
 * rate has reached the room's tick and there is nothing under it that is not a worse game, so
 * what Battery saver drops instead is the **resolution** (see `renderScaleFor`) — which is the
 * larger of the two levers now that the arena is drawn at device pixels, and the one that gives
 * back exactly what the quality fix spent. On a *browser*, where there is no native cap at all,
 * this number is still the whole of what the switch does to the frame rate.
 */
export const BATTERY_SAVER_TARGET_FPS = 20;

/**
 * How far above `NATIVE_TARGET_FPS` the limit is asked for.
 *
 * `TimeStep.stepLimitFPS` fires when the accumulated delta is `>= 1000 / limit`, so a limit of
 * exactly the rate a 60 Hz panel already runs at compares two copies of the same number and
 * drops about half the frames on the one display that had nothing to spare. Two frames of
 * headroom clears a 60 Hz panel outright and still refuses the second half of a 120 Hz one.
 */
export const FRAME_LIMIT_HEADROOM_FPS = 2;

/** `fps.limit`'s own value for "run as fast as the display does". */
export const NO_FRAME_LIMIT = 0;

/**
 * The render profile for a surface, under this player's switch.
 *
 * With Battery saver off, the browser's is today's behaviour written down — Phaser's own
 * defaults, so a desktop keeps every frame its monitor can show — and the shell's is the capped
 * one. With it on, both are capped at BATTERY_SAVER_TARGET_FPS.
 *
 * **The context knobs are deliberately not part of the switch.** `antialiasGL` and
 * `powerPreference` are decided when the WebGL context is created and cannot be changed on a
 * live one, so putting them behind a control a player flips mid-run would mean a setting that
 * applies to the *next* match — the one kind of switch this modal has none of.
 */
export function renderProfile({
  native,
  batterySaver,
  devicePixelRatio,
}: RenderEnvironment): RenderProfile {
  const rate = batterySaver ? BATTERY_SAVER_TARGET_FPS : NATIVE_TARGET_FPS;
  return {
    // Deliberately **not** `rate`. `fps.target` enforces nothing — it is a hint the loop smooths
    // its deltas against — and it is the one field of the two that `applyFrameLimit` cannot move
    // on a live game without reaching for a second undeclared field. Leaving it where it has
    // always been is what makes the switch a complete change rather than most of one.
    targetFps: NATIVE_TARGET_FPS,
    // Battery saver caps every surface; without it, only the shell is capped. The headroom is
    // the same arithmetic at both rates — see FRAME_LIMIT_HEADROOM_FPS.
    frameLimit: batterySaver || native ? rate + FRAME_LIMIT_HEADROOM_FPS : NO_FRAME_LIMIT,
    forceSetTimeOut: false,
    smoothStep: true,
    antialias: true,
    // **Restored on the shell**, and the note above `renderScale` is why it should never have
    // gone: it was removed on the argument that a 1x buffer's edges are smoothed on the way to
    // the screen, which is the opposite of what upscaling does. At MAX_RENDER_SCALE the buffer is
    // already resolving an edge four times as finely, so multisampling on top of it is the cheap
    // half of the fix rather than the whole of it — but it is the half that costs nothing to keep
    // and the half a player notices when the ratio turns out to be 1.
    antialiasGL: true,
    powerPreference: native ? 'low-power' : 'default',
    // Not a cost knob. `roundPixels` rounds every sprite's *render position* to a whole pixel,
    // and on a canvas the compositor is already upscaling, that buys no sharpness and can make a
    // photo skin step a device pixel or three sideways while the camera is easing between zoom
    // bands. It is in the profile so that every value in the `render` block comes from one
    // place; its answer is no.
    roundPixels: false,
    // Every surface, not just the shell: a desktop with a retina display was being upscaled by
    // exactly the same arithmetic, and nothing about a browser made that look better. A ratio
    // below 1, and a browser that reports something absurd, both land on 1 — never on 0, which
    // would be a canvas with no pixels in it.
    renderScale: renderScaleFor(devicePixelRatio, batterySaver),
  };
}

/**
 * Buffer pixels per CSS pixel for a display reporting `devicePixelRatio`.
 *
 * Clamped at both ends. Below 1 there is nothing to gain and a fractional buffer to round; above
 * MAX_RENDER_SCALE there is nothing to see. A non-finite ratio is a browser answering something
 * meaningless, and the honest reading of that is 1.
 */
export function renderScaleFor(devicePixelRatio: number, batterySaver = false): number {
  // The switch's own half: back to one buffer pixel per CSS pixel, which is a quarter of the
  // fragments and the single largest thing a player can hand back. It costs exactly the edges the
  // quality fix bought, which is why it is behind a switch and not a default.
  if (batterySaver) return 1;
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio <= 1) return 1;
  return Math.min(devicePixelRatio, MAX_RENDER_SCALE);
}

/**
 * Which compositor profile a surface gets: `'cheap'` drops the HUD's backdrop blurs and overlay
 * shadows, `'full'` is every one of them.
 *
 * **This is the half of the drain the frame cap could not reach, and it is not a JavaScript cost
 * at all.** With the arena open, this client has fifteen live `backdrop-filter` layers over a
 * WebGL canvas that changes every frame; each one is a copy of the composited backdrop, a
 * multi-pass blur of it at the device's own resolution, and a composite of the result, redone
 * whenever what is behind it moves. That work happens on the GPU and the compositor thread,
 * which is exactly why capping the game step at 30 fps and drawing a cheaper field — the two
 * things already tried — left the battery figure where it was: both of them make the *main
 * thread* cheaper, and the main thread was never what was hot.
 *
 * The rules themselves are in `app.css` under `html[data-compositor='cheap']`, because what they
 * change is how the HUD looks and CSS is where that belongs. This function decides only which of
 * the two a surface is on, and `applyCompositorProfile` is what writes it down.
 *
 * A browser keeps `'full'`: a desktop GPU is not the constraint, and the shell is the surface the
 * drain was reported on.
 */
export function compositorProfile({
  native,
}: Pick<RenderEnvironment, 'native'>): CompositorProfile {
  return native ? 'cheap' : 'full';
}

/** The two compositor profiles. See `compositorProfile`. */
export type CompositorProfile = 'cheap' | 'full';

/**
 * The attribute `app.css` reads, written onto the document element.
 *
 * `'full'` *removes* the attribute rather than setting it, so the default profile is the absence
 * of a marker and no stylesheet rule can be written that only applies to a browser by accident.
 */
export function applyCompositorProfile(
  root: { dataset: DOMStringMap },
  profile: CompositorProfile,
): void {
  if (profile === 'cheap') root.dataset.compositor = 'cheap';
  else delete root.dataset.compositor;
}

/**
 * The frames per second a profile permits on a display of `refreshHz`.
 *
 * Not used by the game — `fps.limit` is what enforces this, inside Phaser. It is here because it
 * is the arithmetic the whole cap rests on, and asserting it against the refresh rates real
 * phones actually have is the difference between believing the cap works and knowing it does.
 */
export function permittedFps(profile: RenderProfile, refreshHz: number): number {
  if (profile.frameLimit === NO_FRAME_LIMIT) return refreshHz;
  // A whole number of rAF ticks has to accumulate past the limit's interval before the step runs.
  const ticksPerStep = Math.ceil(refreshHz / profile.frameLimit);
  return refreshHz / Math.max(1, ticksPerStep);
}

/**
 * As much of Phaser's `TimeStep` as changing the cap on a live game touches.
 *
 * Structural rather than imported, for this module's reason: nothing here may pull Phaser into
 * the bundle, and a fake with these six members is what makes the sequence below assertable.
 */
export interface FrameLimitedLoop {
  /** `TimeStep.running` — false while the loop is asleep, which is a backgrounded app. */
  running: boolean;
  /** `TimeStep.fpsLimit`, the configured cap; 0 is no limit. */
  fpsLimit: number;
  /** `TimeStep.hasFpsLimit`, which is what decides *which step function* the loop binds. */
  hasFpsLimit: boolean;
  sleep(): void;
  wake(seamless?: boolean): void;
}

/**
 * The cap in milliseconds, which is the number `stepLimitFPS` actually compares against.
 *
 * It is `TimeStep._limitRate`, and it is written through a cast because Phaser does not declare
 * it: the field is internal, derived once in the constructor from `fpsLimit`, and there is no
 * setter that re-derives it. Leaving it behind would set a cap the loop then ignored.
 */
type LimitRate = { _limitRate: number };

/**
 * Move a running game's frame cap to `limit` (0 for none).
 *
 * **Phaser has no setter for this, and the reason it needs more than an assignment is where the
 * step function is chosen.** `TimeStep.start` and `TimeStep.wake` both bind either `step` or
 * `stepLimitFPS` off `hasFpsLimit` *at the moment they run*, and nothing re-reads it afterwards
 * — so a loop that was started uncapped keeps calling the unlimited step however many times
 * `fpsLimit` is written. Cycling it through `sleep` / `wake` is what re-binds it, and `wake`
 * returns early on a loop that is already running, so the sleep is not optional.
 *
 * **A sleeping loop is left asleep**, which is the one case that has to be got right: the app is
 * backgrounded, and the visibility handler's own `wake()` will bind the right step from the
 * fields this already wrote. Waking it here would be this function turning the screen's drawing
 * back on behind a locked phone — the exact drain the profile exists to stop.
 */
export function applyFrameLimit(loop: FrameLimitedLoop, limit: number): void {
  if (loop.fpsLimit === limit) return;
  loop.fpsLimit = limit;
  loop.hasFpsLimit = limit > 0;
  (loop as unknown as LimitRate)._limitRate = limit > 0 ? 1000 / limit : 0;
  if (!loop.running) return;
  loop.sleep();
  loop.wake();
}
